import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShieldAlt, 
  faNetworkWired, 
  faLaptopCode, 
  faChartLine, 
  faExclamationTriangle, 
  faHistory,
  faIndustry,
  faTruck,
  faDesktop,
  faCode,
  faCogs
} from '@fortawesome/free-solid-svg-icons';
import './Services.scss';

/**
 * Services component for MET Connect® website
 * 
 * Showcases the services offered by MET Connect®:
 * 1. Remote Access Services
 * 2. Remote Monitoring Services
 * 3. Custom Software Services
 * 4. Specialized expertise areas
 * 
 * Each service includes features, benefits, and a call-to-action
 * 
 * @returns {JSX.Element} The Services component
 */
const Services = () => {
  /**
   * List of features for Remote Access service
   */
  const remoteAccessFeatures = [
    {
      icon: faShieldAlt,
      title: 'Enterprise-Grade Security',
      description: 'Secure communications with end-to-end encryption and multi-factor authentication.'
    },
    {
      icon: faNetworkWired,
      title: 'No Static IP Required',
      description: 'Connect to your network without configuring complex DNS or requiring a static IP.'
    },
    {
      icon: faLaptopCode,
      title: 'Device Independence',
      description: 'Access your systems from any device with our web-based interface or desktop application.'
    }
  ];

  /**
   * List of features for Remote Monitoring service
   */
  const remoteMonitoringFeatures = [
    {
      icon: faChartLine,
      title: 'Real-time Performance Monitoring',
      description: 'Track system performance metrics and health indicators in real-time.'
    },
    {
      icon: faExclamationTriangle,
      title: 'Proactive Alert System',
      description: 'Receive immediate notifications for potential issues before they become critical.'
    },
    {
      icon: faHistory,
      title: 'Historical Data Analysis',
      description: 'Access historical data to identify trends and optimize system performance.'
    }
  ];

  /**
   * List of features for Custom Software service
   */
  const customSoftwareFeatures = [
    {
      icon: faCode,
      title: 'PLC & Ladder Logic Programming',
      description: 'Custom PLC programming and ladder logic development for industrial automation systems.'
    },
    {
      icon: faDesktop,
      title: 'SCADA System Development',
      description: 'Comprehensive SCADA solutions for real-time monitoring and control of industrial processes.'
    },
    {
      icon: faCogs,
      title: 'Industry-Specific Solutions',
      description: 'Specialized software for water treatment, mining, quarrying, and power systems applications.'
    }
  ];

  /**
   * Specialized expertise areas offered by MET Connect®
   */
  const specializationAreas = [
    {
      id: 'plc',
      icon: faIndustry,
      title: "PLC - Process Control",
      subtitle: "Allen-Bradley® Control/Compact Logix™ & Factory Talk™ Suite experts",
      description: "Expert PLC programming, system analysis, and commissioning services with on-site and remote support options.",
      features: [
        "PLC system requirements analysis",
        "Estimating and Budget Management",
        "PLC programming",
        "Off-site electrical and control system cabinet construction",
        "On-site installation and commissioning services",
        "On-site and remote troubleshooting available with MET Connect®® Remote Access Equipment"
      ]
    },
    {
      id: 'networks',
      icon: faNetworkWired,
      title: "Networks and Telemetry",
      subtitle: "EtherNet/IP™ networks, traditional fieldbuses, on-site wireless infrastructure",
      description: "Tailored network solutions with secure remote access platforms for both cloud and on-premises deployments.",
      features: [
        "Tailored network solutions for your site",
        "Supply and configuration of control system network hardware",
        "Secure Hosted Remote Access Platform",
        "Cloud and on-premises options"
      ]
    },
    {
      id: 'mobile',
      icon: faTruck,
      title: "Mobile Plant Specialization",
      subtitle: "Off-grid mobile plant control and monitoring for your 24/7 operations",
      description: "Design and installation services for generator-powered mining and pumping equipment with on-site commissioning.",
      features: [
        "Design and installation services for generator powered crushing, mining and pumping equipment",
        "Electrical power network design and engineering sign-off",
        "On-site commissioning and testing"
      ]
    },
    {
      id: 'hmi',
      icon: faDesktop,
      title: "HMI/SCADA - Process Visibility",
      subtitle: "Rockwell FT View and Ignition SCADA",
      description: "Complete system analysis, programming, and implementation of on-site or hosted SCADA and Historian solutions.",
      features: [
        "Complete system requirements analysis",
        "HMI/SCADA programming",
        "On-site or hosted SCADA and Historian solutions"
      ]
    }
  ];

  return (
    <section className="services-section" id="services">
      <Container>
        <div className="text-center mb-5">
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">
            MET Connect® offers comprehensive remote access, monitoring, and custom software solutions 
            for your industrial control systems.
          </p>
        </div>

        <Row className="services-row">
          {/* Remote Access Service */}
          <Col lg={4} className="mb-4">
            <Card className="service-card h-100">
              <div className="service-card-header remote-access">
                <h3>Remote Access Service</h3>
              </div>
              <Card.Body>
                <p className="service-description">
                  Securely access your control system network from anywhere in the world 
                  with our industry-leading remote access solution.
                </p>
                
                <div className="service-features">
                  {remoteAccessFeatures.map((feature, index) => (
                    <div className="feature-item" key={index}>
                      <div className="feature-icon">
                        <FontAwesomeIcon icon={feature.icon} />
                      </div>
                      <div className="feature-content">
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
              <Link to="/remote-access" className="service-card-footer remote-access">
                Learn More
              </Link>
            </Card>
          </Col>

          {/* Remote Monitoring Service */}
          <Col lg={4} className="mb-4">
            <Card className="service-card h-100">
              <div className="service-card-header remote-monitoring">
                <h3>Remote Monitoring Service</h3>
              </div>
              <Card.Body>
                <p className="service-description">
                  Keep track of your systems' performance and health with our comprehensive 
                  monitoring solution that alerts you before issues become critical.
                </p>
                
                <div className="service-features">
                  {remoteMonitoringFeatures.map((feature, index) => (
                    <div className="feature-item" key={index}>
                      <div className="feature-icon">
                        <FontAwesomeIcon icon={feature.icon} />
                      </div>
                      <div className="feature-content">
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
              <Link to="/remote-monitoring" className="service-card-footer remote-monitoring">
                Learn More
              </Link>
            </Card>
          </Col>

          {/* Custom Software Service */}
          <Col lg={4} className="mb-4">
            <Card className="service-card h-100">
              <div className="service-card-header custom-software">
                <h3>Custom Software</h3>
              </div>
              <Card.Body>
                <p className="service-description">
                  Specialized PLC, SCADA and ladder logic programming for water treatment, mining, 
                  quarrying, power systems and power generation industries.
                </p>
                
                <div className="service-features">
                  {customSoftwareFeatures.map((feature, index) => (
                    <div className="feature-item" key={index}>
                      <div className="feature-icon">
                        <FontAwesomeIcon icon={feature.icon} />
                      </div>
                      <div className="feature-content">
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
              <Link to="/custom-software" className="service-card-footer custom-software">
                Learn More
              </Link>
            </Card>
          </Col>
        </Row>

        <div className="text-center mt-5 mb-5">
          <h2 className="section-title">Our Expertise</h2>
          <p className="section-subtitle">
            With specialized knowledge in industrial automation systems, our team delivers 
            comprehensive solutions tailored to your specific needs.
          </p>
        </div>

        <Row className="expertise-row">
          {specializationAreas.map((area, index) => (
            <Col lg={6} xl={3} key={area.id} className="mb-4">
              <Card className="expertise-card h-100">
                <div className="expertise-image">
                  <div className={`expertise-icon-overlay expertise-${index}`}>
                    <FontAwesomeIcon icon={area.icon} className="expertise-icon" />
                  </div>
                  <div className="image-overlay">
                    <h3>{area.title}</h3>
                  </div>
                </div>
                <Card.Body>
                  <Card.Text className="expertise-subtitle">{area.subtitle}</Card.Text>
                  <Card.Text>{area.description}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default Services;